import { Component, computed, effect, inject, OnInit, Signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { AbstractDynamicDialogComponent } from '../../../../components/dynamic-dialog/abstract-dynamic-dialog.component';
import { InfosGuidanceComponent } from '../../../../components/infos-guidance/infos-guidance.component';
import { FactoringCompany } from '../../../../model/factoring-company.model';
import { InvoiceStore } from '../invoice.store.component';

@Component({
  selector: 'use-factoring-company-modal',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    InfosGuidanceComponent
  ],
  templateUrl: './use-factoring-company-modal.component.html',
  styles: [
    `
      .mat-column-companyName {
        width: 40%;
      }
    `
  ]
})
export class UseFactoringCompanyModalComponent
  extends AbstractDynamicDialogComponent<{ selectFactoringCompanyId: number }>
  implements OnInit
{
  @ViewChild(MatSort) set sort(matSort: MatSort) {
    this.dataSource.sort = matSort;
  }
  dataSource: MatTableDataSource<FactoringCompany> = new MatTableDataSource();
  displayedColumns: string[] = ['selected', 'companyName', 'fullAddress'];

  #dialogRef = inject(MatDialogRef<UseFactoringCompanyModalComponent>);
  form = new FormGroup({
    factoringCompany: new FormControl(null, Validators.required),
    search: new FormControl('')
  });

  factoringCompaniesList: (FactoringCompany & { fullAddress: string })[] = [];
  #invoiceStore = inject(InvoiceStore);

  get factoringCompany() {
    return this.form.controls.factoringCompany;
  }
  get search() {
    return this.form.controls.search;
  }

  searchSignal: Signal<string> = toSignal(this.search.valueChanges);
  filteredCompanies = computed(() =>
    this.factoringCompaniesList.filter((company) => {
      const trimSearch = this.searchSignal()?.trim().toLowerCase() ?? '';
      return company.companyName.toLowerCase().includes(trimSearch);
    })
  );

  constructor() {
    super();

    effect(() => {
      this.factoringCompaniesList = this.#invoiceStore
        .factoringCompanies()
        .map((company) => ({ ...company, fullAddress: this.#getCompanyAddressAsString(company) }));
    });
    effect(() => {
      this.dataSource.data = this.filteredCompanies();
    });
  }

  ngOnInit() {
    this.dynamicDialogService.dialogRefSignal.set(this.#dialogRef);
    this.dataSource.sort = this.sort;

    const selectId = this.data.selectFactoringCompanyId;
    const selectedFactoringCompany = selectId
      ? this.factoringCompaniesList.find((company) => company.id === selectId)
      : null;
    this.factoringCompany.setValue(selectedFactoringCompany);
  }

  saveOrExecuteAction() {
    if (this.form.valid) {
      this.#dialogRef.close({ selectedFactoringCompany: this.factoringCompany.value });
    }
  }

  selectFactoringCompany(factoringCompany: FactoringCompany) {
    this.factoringCompany.setValue(this.factoringCompany.value !== factoringCompany ? factoringCompany : null);
  }

  #getCompanyAddressAsString(company: FactoringCompany): string {
    const addressComponents = [
      company.addressLine1,
      company.addressLine2,
      company.suburb,
      company.state,
      company.city,
      company.postCode,
      company.country
    ];
    const filteredComponents = addressComponents.filter((component) => component && component.trim().length > 0);
    return filteredComponents.join(' ');
  }
}
